import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@1trading/react';
import { Banknote } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useCryptoPriceSymbols } from '@/hooks/queries/system/use-loans';
import { getErrorMessage } from '@/lib/api/error';
import { buildUpdateLoanRequest, type ClientLoan, isValidQuantity, updateLoan } from '@/services/api/loans';
import { runAuditedLoanMutation } from './run-audited-loan-mutation';

const QUANTITY_ERROR = 'Enter a quantity greater than 0, like 123 or 123.45';
const ASSET_ERROR = 'Select an asset';

// Stable fallback while the prices query has no data
const EMPTY_SYMBOLS: string[] = [];

interface AddAssetDialogProps {
  client: ClientLoan;
  onClose: () => void;
  /** Refetch loans after a successful issue */
  onMutated: () => void;
  /** HTTP 403 → the page-level Unauthorised Action dialog */
  onUnauthorised: () => void;
}

// Issue a new currency line to an institution. Mounted only while open,
// so fields reset on every open.
export const AddAssetDialog = ({ client, onClose, onMutated, onUnauthorised }: AddAssetDialogProps) => {
  const fieldId = useId();
  const [asset, setAsset] = useState('');
  const [quantity, setQuantity] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  // Fix: options come from the env-configured crypto-info instance, not the
  // hard-coded production URL legacy shipped.
  const symbolsQuery = useCryptoPriceSymbols();
  const assetOptions = useMemo(() => {
    // Uppercase-normalised exclusion matches the server's uppercase symbol matching
    // so an existing lowercase key still hides its symbol.
    const existing = new Set(Object.keys(client.loans).map((currency) => currency.toUpperCase()));
    return (symbolsQuery.data ?? EMPTY_SYMBOLS).filter((symbol) => !existing.has(symbol.toUpperCase()));
  }, [symbolsQuery.data, client.loans]);

  // Fix: strict decimal check, > 0 required on ISSUE
  const isQuantityValid = isValidQuantity(quantity, { allowZero: false });

  const handleIssue = async () => {
    if (asset === '' || !isQuantityValid) {
      setShowErrors(true);
      return;
    }
    const trimmed = quantity.trim();
    const outcome = await runAuditedLoanMutation({
      title: `Confirm Issue ${trimmed} ${asset} to ${client.clientName}`,
      description: `Proceeding with this action will issue a credit line of ${trimmed} ${asset} to ${client.clientName}. Your action here will be logged and will be auditable.`,
      actionLabel: `Issue ${trimmed} ${asset} to ${client.clientName}`,
      fallbackError: 'Failed to issue loan',
      execute: (comment) => updateLoan(buildUpdateLoanRequest(client, { kind: 'set', currency: asset, quantity: Number.parseFloat(trimmed) }, comment)),
    });
    if (outcome === 'unauthorised') {
      onClose();
      onUnauthorised();
      return;
    }
    if (outcome !== 'success') return;
    toast.success(`Successfully issued ${trimmed} ${asset} to ${client.clientName}`);
    onMutated();
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="size-5" />
            Add a new asset to credit line for {client.clientName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-asset`}>Select an Asset</Label>
            <Select onValueChange={setAsset} value={asset || undefined}>
              <SelectTrigger disabled={symbolsQuery.isPending || symbolsQuery.isError} id={`${fieldId}-asset`}>
                <SelectValue placeholder={symbolsQuery.isPending ? 'Loading assets…' : 'Select an Asset'} />
              </SelectTrigger>
              <SelectContent>
                {assetOptions.map((symbol) => (
                  <SelectItem key={symbol} value={symbol}>
                    {symbol}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {symbolsQuery.isError ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-500">Failed to load assets: {getErrorMessage(symbolsQuery.error)}</span>
                <Button onClick={() => symbolsQuery.refetch()} size="sm" type="button" variant="outline">
                  Retry
                </Button>
              </div>
            ) : null}
            {showErrors && asset === '' ? <p className="text-sm text-red-500">{ASSET_ERROR}</p> : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-quantity`}>Enter quantity</Label>
            <Input id={`${fieldId}-quantity`} inputMode="decimal" onChange={(event) => setQuantity(event.target.value)} value={quantity} />
            {showErrors && !isQuantityValid ? <p className="text-sm text-red-500">{QUANTITY_ERROR}</p> : null}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button onClick={handleIssue} type="button" variant="primary">
            Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
AddAssetDialog.displayName = 'AddAssetDialog';
